import { api, streamSse, type SseEvent } from './client'

export interface AmbientSuggestion {
  title: string
  description: string
  prompt: string
  target: 'topic' | 'outline' | 'script' | 'general'
}

export interface AmbientSuggestionResponse {
  status: 'ready' | 'empty' | 'unavailable' | 'generating' | 'deferred'
  generated_at?: string | null
  suggestions: AmbientSuggestion[]
}

export function getAmbientSuggestions() {
  return api<AmbientSuggestionResponse>('/api/agent/ambient-suggestions')
}

export interface AgentThreadSummary {
  thread_id: string
  created_at?: string
  updated_at?: string
  message_count?: number
  messages?: Array<Record<string, unknown>>
  status?: string
}

export interface AgentFeedbackRecord {
  feedback_id: string
  thread_id: string
  message_index: number
  rating: 'up' | 'down'
  reason?: '内容不准确' | '与需求不符' | '缺少依据' | '表达不清' | '其他' | null
  other_reason?: string | null
  created_at?: string
  updated_at?: string
}

export interface AgentRunRecord {
  run_id: string
  thread_id: string
  status: 'queued' | 'running' | 'completed' | 'unavailable' | 'empty' | 'partial' | 'error' | 'interrupted'
  output?: Record<string, unknown> | null
  error?: string | null
  events?: Array<Record<string, unknown>>
  created_at?: string
  updated_at?: string
}

export async function createAgentThread() {
  return api<AgentThreadSummary>('/api/agent/threads', {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export function listAgentThreads() { return api<AgentThreadSummary[]>('/api/agent/threads') }
export function getAgentThread(threadId: string) { return api<AgentThreadSummary>(`/api/agent/threads/${encodeURIComponent(threadId)}`) }

export function updateAgentProjectState(threadId: string, projectId?: string) {
  return api<Record<string, unknown>>(`/api/agent/threads/${encodeURIComponent(threadId)}/state`, {
    method: 'PATCH',
    body: JSON.stringify({ project_id: projectId || null }),
  })
}

export function streamThreadMessage(threadId: string, body: unknown, onEvent: (event: SseEvent) => void) {
  return streamSse(`/api/agent/threads/${encodeURIComponent(threadId)}/messages`, body, onEvent)
}

/** 将产品反馈持久化到对应的 Agent 运行/线程。 */
export function recordAgentFeedback(body: { thread_id: string; message_index: number; rating: 'up' | 'down'; reason?: '内容不准确' | '与需求不符' | '缺少依据' | '表达不清' | '其他'; other_reason?: string }) {
  return api<{ feedback_id: string; status: 'recorded' }>('/api/agent/feedback', { method: 'POST', body: JSON.stringify(body) })
}

/** 服务端反馈是权威数据；本地任务数据只作为渲染缓存。 */
export function getAgentFeedback(threadId: string) {
  return api<AgentFeedbackRecord[]>(`/api/agent/threads/${encodeURIComponent(threadId)}/feedback`)
}

export function listAgentThreadRuns(threadId: string) {
  return api<AgentRunRecord[]>(`/api/agent/threads/${encodeURIComponent(threadId)}/runs`)
}

export function getAgentRun(runId: string) { return api<AgentRunRecord>(`/api/agent/runs/${encodeURIComponent(runId)}`) }

export function resumeAgentRun(runId: string, approved: boolean, onEvent: (event: SseEvent) => void) {
  return streamSse(`/api/agent/runs/${encodeURIComponent(runId)}/resume`, { approved }, onEvent)
}

export function streamStudioGeneration(route: 'topic' | 'outline' | 'script', body: Record<string, unknown>, onEvent: (event: SseEvent) => void) {
  return streamSse('/api/studio/generate', { route, ...body }, onEvent)
}
